import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';

export interface DocumentoP8ConsultaFiltro {
  top: number;
  className: string;
  query: string;
  order?: string;
}

export interface DocumentoP8ConsultaResultado {
  id?: string;
  document?: string;
  name?: string;
  className?: string;
}

/**
 * Servicio para descargar documentos desde el repositorio P8.
 * 
 * Utiliza el endpoint genérico: GET /api/servicios/documentos/p8/{idP8}
 * 
 * Legacy: ConsumeServiceNoReturnAction.consultaDocumentoPO()
 * 
 * @author GIROP Migration
 */
@Injectable({
  providedIn: 'root'
})
export class DocumentoP8Service {
  
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.backend.baseUrl}/servicios/documentos/p8`;

  async consultarDocumentos(filtro: DocumentoP8ConsultaFiltro): Promise<DocumentoP8ConsultaResultado[]> {
    if (!filtro?.className?.trim() || !filtro?.query?.trim()) {
      throw new Error('Los parámetros className y query son requeridos para consultar documentos.');
    }

    const params: Record<string, string> = {
      top: String(filtro.top),
      className: filtro.className,
      query: filtro.query
    };

    if (filtro.order?.trim()) {
      params['order'] = filtro.order;
    }

    return firstValueFrom(
      this.http.get<DocumentoP8ConsultaResultado[]>(`${this.baseUrl}/search`, { params })
    );
  }
  
  /**
   * Descarga un documento desde P8 y lo abre/descarga en el navegador.
   * 
   * @param idP8 Identificador único del documento en P8
   * @param nombreSugerido Nombre sugerido para el archivo descargado (opcional)
   * @returns Promise que resuelve cuando se inicia la descarga
   */
  async descargar(idP8: string, nombreSugerido?: string): Promise<void> {
    if (!idP8 || idP8.trim() === '') {
      console.error('[P8] ID vacío o nulo recibido:', idP8);
      throw new Error('El identificador del documento es requerido');
    }
    
    const url = `${this.baseUrl}/${encodeURIComponent(idP8)}`;
    console.log('[P8] Iniciando descarga:', { idP8, url });
    
    try {
      // Obtener el documento como blob
      const blob = await firstValueFrom(
        this.http.get(url, {
          responseType: 'blob',
          observe: 'response'
        })
      );
      
      if (!blob.body) {
        throw new Error('No se recibió contenido del servidor');
      }
      
      // Extraer nombre del Content-Disposition si existe
      const contentDisposition = blob.headers.get('Content-Disposition');
      let nombreArchivo = nombreSugerido || 'documento';
      
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^"]+)"?/);
        if (match && match[1]) {
          nombreArchivo = match[1];
        }
      }
      
      // Crear URL del blob y descargar
      const blobUrl = window.URL.createObjectURL(blob.body);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = nombreArchivo;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      
      // Limpiar
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      }, 100);
      
    } catch (error) {
      console.error('[P8] Error al descargar documento:', error);
      if (error instanceof HttpErrorResponse) {
        console.error('[P8] HTTP Error - Status:', error.status, 'Message:', error.message);
        if (error.status === 404) {
          throw new Error('El documento no fue encontrado en el repositorio');
        }
        if (error.status === 401 || error.status === 403) {
          throw new Error('No tiene permisos para acceder al documento');
        }
      }
      throw new Error('Ocurrió un error al descargar el documento. Por favor intente nuevamente.');
    }
  }
  
  /**
   * Abre el documento en una nueva pestaña del navegador.
   * 
   * @param idP8 Identificador único del documento en P8
   * @returns Promise que resuelve cuando se abre la ventana
   */
  async abrirEnNuevaVentana(idP8: string): Promise<void> {
    if (!idP8 || idP8.trim() === '') {
      throw new Error('El identificador del documento es requerido');
    }
    
    try {
      const blob = await firstValueFrom(
        this.http.get(`${this.baseUrl}/${encodeURIComponent(idP8)}`, {
          responseType: 'blob'
        })
      );
      
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      
      // Limpiar después de un tiempo
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 60000); // 1 minuto
      
    } catch (error) {
      if (error instanceof HttpErrorResponse) {
        if (error.status === 404) {
          throw new Error('El documento no fue encontrado en el repositorio');
        }
      }
      throw new Error('Ocurrió un error al abrir el documento. Por favor intente nuevamente.');
    }
  }
}
